import { create } from "zustand";
import type { AppRole } from "../../lib/types";
import { apiClient } from "../../lib/apiClient";
import { authService, isAdminRole, type AuthSession } from "./authService";

export type { AppRole };
export { isAdminRole } from "./authService";

type AuthStatus = "loading" | "authenticated" | "unauthenticated";

type AuthStore = {
  status: AuthStatus;
  session: AuthSession | null;
  role: AppRole | null;
  initialize: () => Promise<void>;
  login: (email: string, password: string) => Promise<AppRole>;
  signup: (email: string, password: string) => Promise<{ status: "signed_in"; role: AppRole } | { status: "confirmation_required" }>;
  refreshProfileRole: () => Promise<AppRole>;
  logout: () => Promise<void>;
};

export const useAuthStore = create<AuthStore>((set, get) => ({
  status: "loading",
  session: null,
  role: null,

  initialize: async () => {
    const session = await authService.getSession().catch(() => null);
    if (!session) {
      set({ status: "unauthenticated", session: null, role: null });
      return;
    }

    set({ session, status: "authenticated" });
    await get().refreshProfileRole();
  },

  login: async (email, password) => {
    const session = await authService.signInWithPassword(email, password);
    set({ session, status: "authenticated" });
    return get().refreshProfileRole();
  },

  signup: async (email, password) => {
    const session = await authService.signUpWithPassword(email, password);
    if (!session) return { status: "confirmation_required" };
    set({ session, status: "authenticated" });
    const role = await get().refreshProfileRole();
    return { status: "signed_in", role };
  },

  refreshProfileRole: async () => {
    const profile = await apiClient.getProfile();
    set({ role: profile.role, status: "authenticated" });
    return profile.role;
  },

  logout: async () => {
    await authService.signOut();
    set({ status: "unauthenticated", session: null, role: null });
  }
}));

export function getAuthRole() {
  return useAuthStore.getState().role;
}
